import { Component, TemplateRef, ChangeDetectionStrategy, ViewEncapsulation, signal, effect } from '@angular/core';
import { input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
    selector: 'fcp-table',
    imports: [CommonModule, TableModule, SkeletonModule],
    styleUrl: './fcp-table.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    templateUrl: './fcp-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    }
})
export class FcpTableComponent {
    e2e = input<string | null>(null);
    value = input<(FcpTableRowData | undefined)[]>([]);
    columns = input<FcpTableColumn[]>();
    loading = input<boolean>(false);
    styleClass = input<string>('fcp-table');

    size = input<FcpTableSize>();
    showGridlines = input<boolean>(false);
    stripedRows = input<boolean>(false);
    responsiveLayout = input<FcpTableResponsiveLayout>();
    tableStyle = input<Record<string, unknown>>();
    tableStyleClass = input<string>();
    scrollable = input<boolean>(false);
    scrollHeight = input<string>();
    virtualScroll = input<boolean>(false);

    headerTemplate = input<TemplateRef<unknown>>();
    bodyTemplate = input<TemplateRef<unknown>>();
    footerTemplate = input<TemplateRef<unknown>>();
    emptyTemplate = input<TemplateRef<unknown>>();
    captionTemplate = input<TemplateRef<unknown>>();

    selectionMode = input<Exclude<FcpTableSelectionMode, FcpTableSelectionMode.Multiple>>();
    selection = input<FcpTableRowData | FcpTableRowData[]>();
    dataKey = input<string>();
    metaKeySelection = input<boolean>(true);

    sortMode = input<FcpTableSortMode>(FcpTableSortMode.Single);
    sortField = input<string>();
    sortOrder = input<number>();
    multiSortMeta = input<FcpTableSortMeta[]>();

    paginator = input<boolean>(false);
    rows = input<number>();
    totalRecords = input<number>();
    first = input<number>(0);
    pageLinks = input<number>(5);
    showFirstLastIcon = input<boolean>(true);
    showPageLinks = input<boolean>(true);
    showJumpToPageDropdown = input<boolean>(false);
    showCurrentPageReport = input<boolean>(false);
    currentPageReportTemplate = input<string>();
    lazy = input<boolean>(false);
    lazyLoadOnInit = input<boolean>(true);

    rowSelect = output<FcpTableSelectionEvent>();
    rowUnselect = output<FcpTableSelectionEvent>();
    sort = output<FcpTableSortEvent>();
    selectionChange = output<FcpTableRowData | FcpTableRowData[]>();
    page = output<FcpTablePageEvent>();
    sortChange = output<FcpTableSortEvent>();
    lazyLoad = output<FcpTableLazyLoadEvent>();

    hasCustomHeader = computed(() => !!this.headerTemplate());
    hasCustomBody = computed(() => !!this.bodyTemplate());
    hasCustomFooter = computed(() => !!this.footerTemplate());
    hasCustomEmpty = computed(() => !!this.emptyTemplate());
    hasCustomCaption = computed(() => !!this.captionTemplate());

    hasColumns = computed(() => !!this.columns() && this.columns()!.length > 0);
    shouldShowDefaultHeader = computed(() => !this.hasCustomHeader() && this.hasColumns());
    shouldShowDefaultBody = computed(() => !this.hasCustomBody() && this.hasColumns());

    sortableColumns = computed(() => this.columns()?.filter((col) => col.sortable) ?? []);

    virtualScrollItemSize = computed<number | null>(() => {
        // Return the height of the table row based on the size of the table according to the design system
        if (this.size() === FcpTableSize.Small) {
            return 48;
        }

        if (this.size() === FcpTableSize.Large) {
            return 104;
        }

        return 64;
    });

    /**
     * PrimeNG defaults this to 250ms and, while delayed, blanks the whole viewport with loaders
     * whenever #loadingbody is present. Keep delay at 0 so only undefined rows use skeletons.
     */
    readonly virtualScrollDelay = 0;

    /**
     * PrimeNG Table hardcodes scroller `autoSize=true`, which shrinks the viewport when the row
     * list is shorter than `scrollHeight`. Keep the configured height for virtual tables.
     */
    readonly virtualScrollOptions = { autoSize: false };

    /** Tracks sort when the parent does not bind sortField/sortOrder (uncontrolled mode). */
    private readonly activeSortField = signal<string | undefined>(undefined);
    private readonly activeSortOrder = signal<number>(1);

    /**
     * Previous bound input values. Used to detect defined → undefined transitions so controlled
     * clears wipe internal state instead of falling back to a stale active sort.
     */
    private lastBoundSortField: string | undefined = undefined;
    private lastBoundSortOrder: number | undefined = undefined;

    resolvedSortField = computed(() => this.sortField() ?? this.activeSortField());
    resolvedSortOrder = computed(() => this.sortOrder() ?? this.activeSortOrder());

    selectionModeEnum = FcpTableSelectionMode;

    totalRecordsForTable = computed(() => this.totalRecords() ?? this.value().length);

    selectionModeForTable = computed<FcpTableSelectionMode | undefined>(() => {
        const mode = this.selectionMode();

        if (mode === FcpTableSelectionMode.MultipleCheckbox) {
            return FcpTableSelectionMode.Multiple;
        }

        return mode;
    });

    readonly sortOrderAscending = 1;
    readonly sortOrderDescending = -1;

    constructor() {
        effect(() => {
            const field = this.sortField();

            if (field !== undefined) {
                this.activeSortField.set(field);
            } else if (this.lastBoundSortField !== undefined) {
                this.activeSortField.set(undefined);
            }

            this.lastBoundSortField = field;
        });

        effect(() => {
            const order = this.sortOrder();

            if (order !== undefined) {
                this.activeSortOrder.set(order);
            } else if (this.lastBoundSortOrder !== undefined) {
                this.activeSortOrder.set(this.sortOrderAscending);
            }

            this.lastBoundSortOrder = order;
        });
    }

    onRowSelect(event: FcpTableSelectionEvent) {
        this.rowSelect.emit(event);
    }

    onRowUnselect(event: FcpTableSelectionEvent) {
        this.rowUnselect.emit(event);
    }

    onSort(event: FcpTableSortEvent) {
        this.activeSortField.set(event.field);
        this.activeSortOrder.set(event.order);
        this.sort.emit(event);
        this.sortChange.emit(event);
    }

    onSelectionChange(event: FcpTableRowData | FcpTableRowData[]) {
        this.selectionChange.emit(event);
    }

    onPage(event: FcpTablePageEvent) {
        this.page.emit(event);
    }

    onLazyLoad(event: FcpTableLazyLoadEvent) {
        this.lazyLoad.emit(event);
    }
}

export enum FcpTableSize {
    Small = 'small',
    Normal = 'normal',
    Large = 'large'
}

export enum FcpTableResponsiveLayout {
    Stack = 'stack',
    Scroll = 'scroll'
}

export enum FcpTableSelectionMode {
    Single = 'single',
    Multiple = 'multiple',
    MultipleCheckbox = 'multiple-checkbox'
}

export enum FcpTableSortMode {
    Single = 'single',
    Multiple = 'multiple'
}

export interface FcpTableColumn {
    field: string;
    header: string;
    sortable?: boolean;
    width?: string;
    styleClass?: string;
    headerStyle?: Record<string, unknown>;
    bodyStyle?: Record<string, unknown>;
    cellTemplate?: TemplateRef<FcpTableCellTemplateContext>;
}

export interface FcpTableRowData {
    [key: string]: unknown;
}

export interface FcpTableCellTemplateContext {
    $implicit: unknown;
    value: unknown;
    rowData: FcpTableRowData;
    column: FcpTableColumn;
    rowIndex: number;
}

export interface FcpTableSortEvent {
    field: string;
    order: number;
    multiSortMeta?: FcpTableSortMeta[];
}

export interface FcpTableSortMeta {
    field: string;
    order: number;
}

export interface FcpTableSelectionEvent {
    originalEvent: Event;
    data: FcpTableRowData;
    index: number;
    type: 'row' | 'checkbox' | 'radio';
}

export interface FcpTablePageEvent {
    first: number;
    rows: number;
    page: number;
    pageCount: number;
}

export type FcpTableLazyLoadEvent = TableLazyLoadEvent;
